import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useCategories, Category } from "@/hooks/use-categories";
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileSpreadsheet } from "lucide-react";
import * as LucideIcons from "lucide-react";

const dreSectionLabels: Record<string, string> = {
  receita_operacional: "Receita Operacional",
  receita_financeira: "Receita Financeira",
  outras_receitas: "Outras Receitas",
  custo_operacional: "Custo Operacional",
  despesa_administrativa: "Desp. Administrativa",
  despesa_comercial: "Desp. Comercial",
  despesa_pessoal: "Desp. Pessoal",
  despesa_financeira: "Desp. Financeira",
  outras_despesas: "Outras Despesas",
};

const incomeSections = ["receita_operacional", "receita_financeira", "outras_receitas"];
const expenseSections = [
  "custo_operacional",
  "despesa_administrativa",
  "despesa_comercial",
  "despesa_pessoal",
  "despesa_financeira",
  "outras_despesas",
];

const UNMAPPED_VALUE = "__unmapped__";

function renderCategoryIcon(iconName: string, color: string, className: string = "h-4 w-4") {
  const IconComponent = (LucideIcons as any)[iconName];
  if (!IconComponent) {
    const FallbackIcon = (LucideIcons as any)["CircleDot"];
    return <FallbackIcon className={className} style={{ color }} />;
  }
  return <IconComponent className={className} style={{ color }} />;
}

export function DreCategoryMappingSection() {
  const { categories, loading: categoriesLoading } = useCategories("all");
  const { toast } = useToast();
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Load existing mappings
  useEffect(() => {
    const fetchMappings = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await (supabase as any)
          .from("category_dre_mapping")
          .select("category_id, dre_section");

        if (error) throw error;

        const map: Record<string, string> = {};
        (data || []).forEach((row: any) => {
          map[row.category_id] = row.dre_section;
        });
        setMappings(map);
      } catch (error) {
        console.error("Error fetching DRE mappings:", error);
      } finally {
        setLoadingMappings(false);
      }
    };
    fetchMappings();
  }, []);

  const handleMappingChange = async (categoryId: string, dreSection: string) => {
    setSavingId(categoryId);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario nao autenticado");

      if (dreSection === UNMAPPED_VALUE) {
        // Remove mapping
        const { error } = await (supabase as any)
          .from("category_dre_mapping")
          .delete()
          .eq("category_id", categoryId);

        if (error) throw error;

        setMappings(prev => {
          const next = { ...prev };
          delete next[categoryId];
          return next;
        });
      } else {
        // Upsert mapping
        const { error } = await (supabase as any)
          .from("category_dre_mapping")
          .upsert(
            {
              user_id: user.id,
              category_id: categoryId,
              dre_section: dreSection,
            },
            { onConflict: "user_id,category_id" }
          );

        if (error) throw error;

        setMappings(prev => ({ ...prev, [categoryId]: dreSection }));
      }

      toast({
        title: "Mapeamento salvo",
        description: "A classificacao DRE foi atualizada.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar mapeamento",
        description: error.message,
      });
    } finally {
      setSavingId(null);
    }
  };

  const isLoading = categoriesLoading || loadingMappings;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const incomeCategories = categories.filter(c => c.type === "income");
  const expenseCategories = categories.filter(c => c.type === "expense");

  const unmappedCount = categories.filter(c => !mappings[c.id]).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Classificacao DRE
            </CardTitle>
            <CardDescription>
              Mapeie suas categorias para as secoes do DRE (Demonstrativo de Resultado)
            </CardDescription>
          </div>
          {unmappedCount > 0 && (
            <Badge variant="outline" className="text-yellow-600 border-yellow-600">
              {unmappedCount} sem classificacao
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Income Categories */}
        {incomeCategories.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Categorias de Receita
            </h3>
            <div className="space-y-2">
              {incomeCategories.map(category => (
                <CategoryMappingRow
                  key={category.id}
                  category={category}
                  sections={incomeSections}
                  currentMapping={mappings[category.id]}
                  isSaving={savingId === category.id}
                  onMappingChange={handleMappingChange}
                />
              ))}
            </div>
          </div>
        )}

        {/* Expense Categories */}
        {expenseCategories.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Categorias de Despesa
            </h3>
            <div className="space-y-2">
              {expenseCategories.map(category => (
                <CategoryMappingRow
                  key={category.id}
                  category={category}
                  sections={expenseSections}
                  currentMapping={mappings[category.id]}
                  isSaving={savingId === category.id}
                  onMappingChange={handleMappingChange}
                />
              ))}
            </div>
          </div>
        )}

        {categories.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Nenhuma categoria encontrada.</p>
            <p className="text-sm">Crie categorias para mapear ao DRE.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface CategoryMappingRowProps {
  category: Category;
  sections: string[];
  currentMapping?: string;
  isSaving: boolean;
  onMappingChange: (categoryId: string, dreSection: string) => void;
}

function CategoryMappingRow({
  category,
  sections,
  currentMapping,
  isSaving,
  onMappingChange,
}: CategoryMappingRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="flex items-center justify-center h-8 w-8 rounded-md shrink-0"
          style={{ backgroundColor: category.color + "20" }}
        >
          {renderCategoryIcon(category.icon, category.color)}
        </div>
        <span className="text-sm font-medium truncate">{category.name}</span>
        {!currentMapping && (
          <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-600 shrink-0">
            Sem classificacao
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isSaving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        <Select
          value={currentMapping || UNMAPPED_VALUE}
          onValueChange={(value) => onMappingChange(category.id, value)}
          disabled={isSaving}
        >
          <SelectTrigger className="w-[200px] h-8 text-xs">
            <SelectValue placeholder="Selecionar secao..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNMAPPED_VALUE}>
              <span className="text-muted-foreground">Sem classificacao</span>
            </SelectItem>
            {sections.map(section => (
              <SelectItem key={section} value={section}>
                {dreSectionLabels[section]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
